/**
 * Compile / parse helpers for the JSONata object-construction
 * expressions used by the workflow-level `outputMapping`.
 *
 * The workflow's `outputMapping` is a SINGLE JSONata expression
 * evaluated against `{ __input, <nodeKey>: <output>, ... }` to compose
 * `Run.output`. To author it with the same Connect-mode rows UI the
 * input mapper uses, we round-trip between:
 *
 *   rows view (UI):
 *     [
 *       { key: 'answer', value: '$.classify.code' },     // a JSONata ref
 *       { key: 'severity', value: 5 },                   // a literal
 *     ]
 *
 *   record (in-memory, same shape as inputMapping):
 *     { answer: '$.classify.code', severity: 5 }
 *
 *   JSONata object expression (persisted in `WorkflowVersion.outputMapping`):
 *     { "answer": $.classify.code, "severity": 5 }
 *
 * `compileJsonataObject` always succeeds — given any
 * `Record<string, unknown>` it produces a valid JSONata object expr.
 * `parseJsonataObject` is the partial inverse — it succeeds ONLY for
 * the restricted shape produced by `compileJsonataObject` (top-level
 * object, scalar literals or single-segment top-level refs). For
 * anything more complex (nested objects, array literals, computed
 * expressions, filter predicates) it returns `null` and the UI falls
 * back to Advanced mode.
 *
 * The on-disk wire shape is unchanged: still a JSONata string; the
 * runner evaluates it exactly as before. This module only handles the
 * UI lens over that string.
 */

import { formatTopLevelRef, parseTopLevelRef } from './jsonata-ref';

/**
 * Compile a `Record<string, unknown>` of {key → JSONata-ref-or-literal}
 * into a JSONata object-construction expression.
 *
 *   - string values starting with `$` are emitted verbatim (JSONata refs)
 *   - all other values are JSON-stringified (so they become JSONata
 *     string / number / boolean / null literals — JSONata's literal
 *     syntax overlaps with JSON for these cases)
 *
 * Returns `''` for an empty record so the caller can persist `null`
 * (the "no mapping → last terminal node wins" sentinel the runner
 * understands).
 */
export function compileJsonataObject(mapping: Record<string, unknown>): string {
  const keys = Object.keys(mapping);
  if (keys.length === 0) return '';
  const entries = keys
    .filter((k) => k !== '')
    .map((k) => {
      const v = mapping[k];
      const rhs = typeof v === 'string' && v.startsWith('$') ? v : JSON.stringify(v);
      return `  ${JSON.stringify(k)}: ${rhs}`;
    });
  return `{\n${entries.join(',\n')}\n}`;
}

/**
 * Parse a JSONata object-construction expression back into
 * `Record<string, unknown>` of the same shape the input mapping uses.
 * Returns `null` when the expression doesn't fit the restricted shape
 * `compileJsonataObject` produces — the caller renders Advanced mode
 * in that case so we never lossy-edit a complex expression.
 *
 * Supported shapes per entry value:
 *   - JSON string / number / boolean / null literal
 *   - A top-level JSONata ref understood by `parseTopLevelRef`
 *     (`$.foo`, `$.foo.bar`, `` $.`hyphen-key`.field ``, etc.)
 *
 * Not supported (return null): nested objects/arrays, function calls,
 * arithmetic, ternaries, anything with parentheses or brackets.
 */
export function parseJsonataObject(expr: string): Record<string, unknown> | null {
  const trimmed = expr.trim();
  if (trimmed === '') return {};
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;

  const inner = trimmed.slice(1, -1).trim();
  if (inner === '') return {};

  // Split on top-level commas. Tracks bracket depth and string state so
  // commas inside JSON strings or inside a (rejected later) nested
  // structure don't fool us. Brackets/braces/parens at depth > 0 will
  // ultimately cause parseEntry to fail — we still need accurate
  // splitting to even reach that check.
  const segments = splitTopLevelCommas(inner);
  if (!segments) return null;

  const out: Record<string, unknown> = {};
  for (const seg of segments) {
    const entry = parseEntry(seg);
    if (!entry) return null;
    if (entry.key in out) return null; // duplicate key — can't round-trip
    out[entry.key] = entry.value;
  }
  return out;
}

// ─── internals ────────────────────────────────────────────────────────────

/**
 * Split a string by top-level commas, honoring JSON-style double-quoted
 * strings, backtick-quoted JSONata identifiers, and bracket/brace/paren
 * depth. Returns null if any structural mismatch is seen — that means
 * the expression is malformed or too complex for our parser.
 */
function splitTopLevelCommas(s: string): string[] | null {
  const out: string[] = [];
  let depth = 0;
  let inStr: '"' | '`' | null = null;
  let escaped = false;
  let last = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inStr) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (c === '\\' && inStr === '"') {
        escaped = true;
        continue;
      }
      if (c === inStr) inStr = null;
      continue;
    }
    if (c === '"' || c === '`') {
      inStr = c;
      continue;
    }
    if (c === '{' || c === '[' || c === '(') depth++;
    else if (c === '}' || c === ']' || c === ')') {
      depth--;
      if (depth < 0) return null;
    } else if (c === ',' && depth === 0) {
      out.push(s.slice(last, i));
      last = i + 1;
    }
  }
  if (inStr || depth !== 0) return null;
  out.push(s.slice(last));
  return out.map((seg) => seg.trim()).filter((seg) => seg.length > 0);
}

/**
 * Parse a single `"key": value` entry. Returns null when the key
 * isn't a JSON string literal or the value isn't a supported scalar
 * literal / top-level ref.
 */
function parseEntry(seg: string): { key: string; value: unknown } | null {
  // Key must be a JSON-encoded double-quoted string. We don't accept
  // bare-identifier keys because we always emit quoted on the compile
  // side — accepting both would force us to handle JSONata
  // identifier-quoting rules in the parser, which we don't need.
  const colonAt = findTopLevelColon(seg);
  if (colonAt < 0) return null;
  const rawKey = seg.slice(0, colonAt).trim();
  const rawVal = seg.slice(colonAt + 1).trim();
  if (!rawKey.startsWith('"')) return null;
  let key: unknown;
  try {
    key = JSON.parse(rawKey);
  } catch {
    return null;
  }
  if (typeof key !== 'string') return null;

  if (rawVal.startsWith('$')) {
    // JSONata ref — must be a parseable top-level ref. We store it
    // back in the canonical formatted form so a save-then-load cycle
    // is stable (e.g. `$."foo".bar` → `$.foo.bar`).
    const ref = parseTopLevelRef(rawVal);
    if (!ref) return null;
    return { key, value: formatTopLevelRef(ref.sourceKey, ref.field) };
  }
  // JSON literal — but reject objects/arrays so callers know to fall
  // back to Advanced for compound shapes.
  if (rawVal.startsWith('{') || rawVal.startsWith('[')) return null;
  try {
    const value: unknown = JSON.parse(rawVal);
    if (typeof value === 'object' && value !== null) return null;
    return { key, value };
  } catch {
    return null;
  }
}

/**
 * Find the first top-level `:` (not inside a string) in `seg`. JSONata
 * `:` only appears in object-entry separators within our restricted
 * grammar, so the first one is the entry's separator.
 */
function findTopLevelColon(seg: string): number {
  let inStr: '"' | '`' | null = null;
  let escaped = false;
  for (let i = 0; i < seg.length; i++) {
    const c = seg[i];
    if (inStr) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (c === '\\' && inStr === '"') {
        escaped = true;
        continue;
      }
      if (c === inStr) inStr = null;
      continue;
    }
    if (c === '"' || c === '`') {
      inStr = c;
      continue;
    }
    if (c === ':') return i;
  }
  return -1;
}
